import React from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase, FaMapMarkerAlt, FaTasks, FaCode, FaLaptopCode, FaServer } from 'react-icons/fa';

const areaStyles = {
    'Business Development': 'bg-pink-100 text-pink-700',
    'Project Management': 'bg-gray-200 text-gray-800',
    'Software Development': 'bg-olive-100 text-olive-700',
};

const experiences = [
    {
        title: "Business Development Manager & Project Manager",
        company: "CoreTech",
        location: "Nablus, Rafidia",
        period: "December 2025 - Present",
        points: [
            "Develop new business: identify new markets, clients and partnerships, represent the company at conferences and build a professional network.",
            "Meet customers and prospects, and prepare statements of work, proposals and project plans.",
            "Manage IT and website development projects end-to-end — planning, scheduling, execution, delivery and post-delivery support.",
            "Translate business requirements into technical tasks, user stories and clear deliverables, and keep clients, developers and stakeholders aligned.",
            "Provide technical consultancy on software architecture, low-code enterprise platforms and web/mobile solutions.",
        ],
        areas: ["Business Development", "Project Management"],
        icon: <FaTasks className="text-pink-600 text-2xl" />,
        color: "from-pink-100 to-pink-100"
    },
    {
        title: "Software Developer",
        company: "Experts Turnkey Solutions",
        location: "Ramallah, Batn El-Hawa",
        period: "December 2024 - May 2025",
        description: "Specialized in low-code development using Appian. Designed and implemented a fully integrated Deposits Management System. Contributed to Employee Facilitation System for a major bank.",
        areas: ["Software Development"],
        icon: <FaCode className="text-olive-600 text-2xl" />,
        color: "from-olive-100 to-olive-100"
    },
    {
        title: "Back End Developer (Internship)",
        company: "NOURSOFT",
        location: "Nablus",
        period: "February 2024 - May 2024",
        description: "Back-end development with ASP.NET and the ABP framework.",
        areas: ["Software Development"],
        icon: <FaServer className="text-olive-600 text-2xl" />,
        color: "from-olive-100 to-olive-100"
    },
    {
        title: "Operations Supervisor",
        company: "Injaz Organization",
        location: "Nablus, Rafidia",
        period: "January 2023 - August 2023",
        description: "Created detailed UI scripts based on design flow. Developed front-end components using React.js. Designed prototypes using Figma.",
        areas: ["Project Management", "Software Development"],
        icon: <FaLaptopCode className="text-gray-700 text-2xl" />,
        color: "from-gray-200 to-gray-200"
    }
];

const Experience = () => {
    return (
        <section id="experience" className="py-20 bg-cream">
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="mb-12 text-center"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                        <FaBriefcase className="text-pink-600 text-2xl" />
                    </div>
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Work Experience</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-rose-500 mx-auto"></div>
                </motion.div>

                <div className="relative mx-auto max-w-4xl">
                    <div className="hidden md:block absolute left-[7px] top-2 bottom-2 w-0.5 bg-cream-200"></div>
                    
                    {experiences.map((exp, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            viewport={{ once: true }}
                            className="mb-8 relative md:pl-10"
                        >
                            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-l-4 border-pink-500">
                                <div className="flex items-start mb-4">
                                    <div className={`p-3 rounded-lg bg-gradient-to-r ${exp.color} mr-4`}>
                                        {exp.icon}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex flex-col gap-2 md:flex-row md:items-center justify-between mb-2">
                                            <h3 className="text-xl font-bold text-gray-800">{exp.title}</h3>
                                            <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium">
                                                {exp.period}
                                            </span>
                                        </div>
                                        <div className="flex items-center text-gray-600 mb-3">
                                            <span className="font-semibold text-gray-700">{exp.company}</span>
                                            <span className="mx-2">•</span>
                                            <FaMapMarkerAlt className="text-pink-500 mr-1" />
                                            <span>{exp.location}</span>
                                        </div>
                                        {exp.points ? (
                                            <ul className="space-y-2 text-gray-600 leading-relaxed">
                                                {exp.points.map((point) => (
                                                    <li key={point} className="flex gap-3">
                                                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-500" aria-hidden="true" />
                                                        {point}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-gray-600 leading-relaxed">
                                                {exp.description}
                                            </p>
                                        )}
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {exp.areas.map((area) => (
                                                <span key={area} className={`rounded-full px-3 py-1 text-xs font-semibold ${areaStyles[area]}`}>
                                                    {area}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="hidden md:block absolute top-7 left-0 w-4 h-4 bg-pink-500 rounded-full border-4 border-cream"></div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Experience;